import { MouseEvent, WheelEvent, useEffect, useRef } from 'react'
import { getNarrativeConfig, getNarrativeSortRank } from '../utils/category'
import type { NarrativeInfo } from '../types/event'

interface CategoryTabsProps {
  narratives: NarrativeInfo[]
  selectedNarrative?: string
  onNarrativeChange: (narrative?: string) => void
  isLoading?: boolean
}

export function CategoryTabs({
  narratives,
  selectedNarrative,
  onNarrativeChange,
  isLoading,
}: CategoryTabsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, dragged: false })
  const totalEvents = narratives.reduce((sum, n) => sum + n.event_count, 0)
  const visibleNarratives = [...narratives]
    .filter((n) => n.event_count > 0)
    .sort((a, b) => {
      const rankDiff = getNarrativeSortRank(a.name) - getNarrativeSortRank(b.name)
      if (rankDiff !== 0) return rankDiff
      return a.name.localeCompare(b.name)
    })

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.style.scrollbarWidth = 'none'
      ;(container.style as CSSStyleDeclaration & { msOverflowStyle?: string }).msOverflowStyle = 'none'
    }
  }, [])

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current
    if (!container) return
    if (container.scrollWidth <= container.clientWidth) return

    event.preventDefault()
    container.scrollLeft += event.deltaY || event.deltaX
  }

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current
    if (!container) return

    dragStateRef.current = {
      isDown: true,
      startX: event.pageX - container.offsetLeft,
      scrollLeft: container.scrollLeft,
      dragged: false,
    }
  }

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current
    const dragState = dragStateRef.current
    if (!container || !dragState.isDown) return

    event.preventDefault()
    const x = event.pageX - container.offsetLeft
    const walk = x - dragState.startX
    if (Math.abs(walk) > 4) dragState.dragged = true
    container.scrollLeft = dragState.scrollLeft - walk
  }

  const stopDrag = () => {
    dragStateRef.current.isDown = false
  }

  const handleTabClick = (callback: () => void) => {
    if (dragStateRef.current.dragged) {
      dragStateRef.current.dragged = false
      return
    }
    callback()
  }

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
      <div
        ref={scrollContainerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 hide-scrollbar cursor-grab active:cursor-grabbing select-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <button
          onClick={() => handleTabClick(() => onNarrativeChange(undefined))}
          disabled={isLoading}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
            !selectedNarrative
              ? 'bg-blue-500 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          } ${isLoading ? 'opacity-50' : ''}`}
        >
          <span>🌐</span>
          <span>All</span>
          <span className="opacity-70">{totalEvents}</span>
        </button>

        {visibleNarratives.map((narrative) => {
          const config = getNarrativeConfig(narrative.name)
          const isSelected = selectedNarrative === narrative.name

          return (
            <button
              key={narrative.name}
              onClick={() => handleTabClick(() => onNarrativeChange(narrative.name))}
              disabled={isLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                isSelected
                  ? 'bg-blue-500 text-white shadow-sm'
                  : `${config.bgColor} ${config.color} hover:opacity-80`
              } ${isLoading ? 'opacity-50' : ''}`}
            >
              <span>{config.emoji}</span>
              <span>{config.shortName}</span>
              <span className="opacity-70">{narrative.event_count}</span>
            </button>
          )
        })}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}
